'use client'

import { RtLegProvider } from '@/src/contexts/RtLegContext'
import { useResetSmartPreferences } from '@/src/hooks/srp/aiRecommendation'
import { useClearFilters } from '@/src/hooks/srp/clearFilters'
import { DeviceType } from '@/src/types/deviceType'
import type { RtLegIndex } from '@/src/types/srpJourneyIndex'
import { TranslationFunction } from '@/src/types/translations'
import { Box, Button } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface RtSplitFilterHeaderProps {
    tHeader: TranslationFunction<'header'>
    tCommon: TranslationFunction<'common'>
    deviceType: DeviceType
}

const LEGS: RtLegIndex[] = [0, 1]

/**
 * One leg's "X of Y flights" count. Mounted inside its own RtLegProvider so
 * useSearchResults()/useClearFilters()/useResetSmartPreferences() resolve to
 * this leg. It reports its applied-state + clear fn up so the parent can drive a
 * single "Clear all" that spans both legs.
 */
const LegCountSegment = React.memo(
    ({
        leg,
        label,
        tHeader,
        tCommon,
        onRegister
    }: {
        leg: RtLegIndex
        label: string
        tHeader: TranslationFunction<'header'>
        tCommon: TranslationFunction<'common'>
        onRegister: (leg: RtLegIndex, isApplied: boolean, clear: () => void) => void
    }) => {
        // const { numberOfFlightsAfterFilter, totalFlights } = useSearchResults()
        const { isFilterApplied } = useClearFilters()
        const { resetAIStatesAndClearAllFiltersForCurrentLeg } = useResetSmartPreferences()

        useEffect(() => {
            onRegister(leg, isFilterApplied, resetAIStatesAndClearAllFiltersForCurrentLeg)
        }, [leg, isFilterApplied, resetAIStatesAndClearAllFiltersForCurrentLeg, onRegister])
        return null
        // return (
        //     <Typography variant="body2" sx={{ color: 'grey.600', whiteSpace: 'nowrap' }}>
        //         <Typography component="span" sx={{ fontWeight: 700, color: 'grey.700', mr: 0.5 }}>
        //             {label}
        //         </Typography>
        //         <Typography component="span" sx={{ fontWeight: 600 }}>
        //             {numberOfFlightsAfterFilter}
        //         </Typography>
        //         <Typography component="span" sx={{ mx: 0.5 }}>
        //             {tCommon('of')}
        //         </Typography>
        //         <Typography component="span" sx={{ fontWeight: 600 }}>
        //             {totalFlights ?? 0}
        //         </Typography>
        //         <Typography component="span" sx={{ ml: 0.5 }}>
        //             {tHeader('flights')}
        //         </Typography>
        //     </Typography>
        // )
    }
)

LegCountSegment.displayName = 'LegCountSegment'

/**
 * Split-view filter header: shows both legs' filtered counts side by side and a
 * single "Clear all filters" that clears whichever legs have filters applied.
 *
 * The plain FilterHeader can't be reused here because it reads a single leg from
 * RtLegContext (defaulting to leg 0), so in split view it only ever showed the
 * onward count and its Clear only cleared onward.
 */
const RtSplitFilterHeader = React.memo(({ tHeader, tCommon, deviceType }: RtSplitFilterHeaderProps) => {
    const clearFnsRef = useRef<Partial<Record<RtLegIndex, () => void>>>({})
    const [applied, setApplied] = useState<Record<RtLegIndex, boolean>>({ 0: false, 1: false })

    const registerLeg = useCallback((leg: RtLegIndex, isApplied: boolean, clear: () => void) => {
        clearFnsRef.current[leg] = clear
        setApplied(prev => (prev[leg] === isApplied ? prev : { ...prev, [leg]: isApplied }))
    }, [])

    const anyApplied = applied[0] || applied[1]

    const handleClearAll = useCallback(() => {
        LEGS.forEach(leg => {
            if (applied[leg]) clearFnsRef.current[leg]?.()
        })
    }, [applied])

    return (
        <Box
            sx={{
                px: 2,
                // py: 1.5,
                // borderBottom: 1,
                // borderColor: 'grey.200',
                bgcolor: 'background.default',
                position: deviceType === 'MOBILE' ? 'sticky' : 'static',
                top: deviceType === 'MOBILE' ? 0 : 'auto',
                zIndex: deviceType === 'MOBILE' ? 1 : 'auto'
            }}
        >
            {anyApplied && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={handleClearAll}
                        sx={{ fontWeight: 600, whiteSpace: 'nowrap', p: 0, minWidth: 'auto' }}
                    >
                        {tCommon('clearFilters')}
                    </Button>
                </Box>
            )}
            <Box >
                <RtLegProvider leg={0}>
                    <LegCountSegment leg={0} label={tCommon('onward')} tHeader={tHeader} tCommon={tCommon} onRegister={registerLeg} />
                </RtLegProvider>
                <RtLegProvider leg={1}>
                    <LegCountSegment leg={1} label={tCommon('return')} tHeader={tHeader} tCommon={tCommon} onRegister={registerLeg} />
                </RtLegProvider>
            </Box>
        </Box>
    )
})

RtSplitFilterHeader.displayName = 'RtSplitFilterHeader'

export default RtSplitFilterHeader
